import {
    type AddressInput,
    type AddressFormat,
    type AddressInputKey,
    type AddressInputSource,
    type AddressFieldWithCountry,
    type ExtendedAddressInputKey,
    toAddressInputKey,
    addressInputKeys,
    updateAddressInput,
    getAddressFieldMaxLength,
} from '@precon-tools-suite-by-shared/js-commerceguys-addressing';
import { useAddressInput, UseAddressInputOptions } from './address-inputs';
import useVuelidate, {
    type GlobalConfig,
    type ErrorObject,
} from '@vuelidate/core';
import { maxLength, requiredIf, helpers } from '@vuelidate/validators';
import { MaybeRefOrGetter, toValue } from 'vue';
import { computed } from 'vue';

export type UseAddressInputRulesOptions = {
    strict?: MaybeRefOrGetter<boolean>;
    partial?: MaybeRefOrGetter<boolean>;
};
export type UseAddressInputWithValidationOptions = UseAddressInputOptions &
    GlobalConfig &
    UseAddressInputRulesOptions;

export function useAddressInputRules(
    format: MaybeRefOrGetter<AddressFormat | null | undefined>,
    options?: UseAddressInputRulesOptions
) {
    return computed(() => {
        const result = Object.fromEntries(
            addressInputKeys.map((key) => {
                const rules = {
                    maxLength: maxLength(getAddressFieldMaxLength(key)),
                    required: requiredIf(
                        () =>
                            !!(
                                key === 'countryCode' ||
                                (!toValue(options?.partial) &&
                                    !!toValue(format)?.requires(key))
                            )
                    ),
                    unused: (value: string | null | undefined) => {
                        if (!toValue(options?.strict)) return true;
                        const f = toValue(format);
                        if (!f || !f.uses(key)) return true;
                        return !helpers.req(value);
                    },
                };

                return [key, rules];
            })
        ) as unknown as Record<AddressInputKey, Record<string, any>>;

        result.postalCode.matchesPattern = (
            value: string | null | undefined
        ) => {
            if (!helpers.req(value)) return true;
            const f = toValue(format);
            if (!f || !f.uses('postalCode')) return true;
            return f.testPostalCode(value as string);
        };

        return result;
    });
}

export function useAddressInputWithValidation(
    source?: MaybeRefOrGetter<AddressInputSource>,
    options?: UseAddressInputWithValidationOptions
) {
    const address = useAddressInput(source, options);

    const rules = useAddressInputRules(
        () => address.country.value?.addressFormat,
        options
    );

    const v = useVuelidate<AddressInput>(rules, address, options);

    function fieldHasError(
        field: AddressFieldWithCountry | ExtendedAddressInputKey
    ): boolean {
        const inputKey = toAddressInputKey(field);
        if (!inputKey) return false;
        return v.value[inputKey]?.$error ?? false;
    }

    function getFieldErrors(
        field: AddressFieldWithCountry | ExtendedAddressInputKey
    ): ErrorObject[] {
        const inputKey = toAddressInputKey(field);
        if (!inputKey) return [];
        return v.value[inputKey]?.$errors ?? [];
    }

    function updateValue(source: AddressInputSource) {
        return updateAddressInput(v.value.$model, source);
    }

    function updateFieldValue(
        field: AddressFieldWithCountry | ExtendedAddressInputKey,
        value: string | null
    ): boolean {
        const inputKey = toAddressInputKey(field);
        if (!inputKey) return false;
        return updateValue({ [inputKey]: value });
    }

    return {
        address,
        v,
        country: address.country,
        updateValue,
        updateFieldValue,
        getFieldValue: address.getField,
        setField: address.setField,
        fieldHasError,
        getFieldErrors,
    };
}
